import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { appState, type AppStateData } from '../store/AppState';
import { api } from '../services/api';

@customElement('sync-indicator')
export class SyncIndicator extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private isOnline = true;

  @state()
  private pendingCount = 0;

  @state()
  private isAdmin = false;

  @state()
  private pendingApprovals = 0;

  private checkInterval: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    appState.subscribe((state: AppStateData) => {
      this.isOnline = state.isOnline;
      this.pendingCount = state.pendingCount;
      this.isAdmin = state.auth.user?.is_admin ?? false;

      // Check for pending approvals when admin status changes
      if (this.isAdmin && this.isOnline) {
        this.checkPendingApprovals();
      }
    });

    // Set up periodic check for pending approvals (every 60 seconds)
    this.checkInterval = window.setInterval(() => {
      if (this.isAdmin && this.isOnline) {
        this.checkPendingApprovals();
      }
    }, 60000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  private async checkPendingApprovals() {
    try {
      const pending = await api.getPendingGames();
      this.pendingApprovals = pending.length;
    } catch {
      // Silently fail - not critical
    }
  }

  render() {
    const status = this.getStatus();

    return html`
      <div class="flex items-center gap-2 px-2">
        <!-- Connection/Sync status -->
        <div class="flex items-center gap-1.5">
          <span class="sync-dot ${status.class}"></span>
          <span class="text-sm text-base-content/70">${status.text}</span>
        </div>

        <!-- Pending sync indicator with spinner -->
        ${this.pendingCount > 0 && this.isOnline
          ? html`
              <div class="badge badge-info badge-sm gap-1">
                <span class="loading loading-spinner loading-xs"></span>
                ${this.pendingCount}
              </div>
            `
          : nothing}

        <!-- Pending approvals badge (admin only) -->
        ${this.isAdmin && this.pendingApprovals > 0
          ? html`
              <div class="indicator">
                <span class="indicator-item badge badge-secondary badge-xs">${this.pendingApprovals}</span>
                <button
                  class="btn btn-ghost btn-xs"
                  @click=${this.navigateToApprovals}
                  title="Pending game approvals"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private getStatus() {
    if (!this.isOnline) {
      return { class: 'offline', text: 'Offline' };
    }
    if (this.pendingCount > 0) {
      return { class: 'pending', text: 'Syncing...' };
    }
    return { class: 'synced', text: 'Synced' };
  }

  private navigateToApprovals() {
    // Navigate directly to admin approvals view (?tab=admin&view=approvals)
    appState.setActiveTab('admin');
    appState.updateUrlWithView('approvals');
    // Dispatch event for AdminView to switch to approvals tab
    this.dispatchEvent(
      new CustomEvent('navigate-approvals', {
        bubbles: true,
        composed: true,
      })
    );
  }
}
